import { getRole } from '@/store/selectors';
import { setScrollRowIndex } from '@/store/ui/actions';
import { QueryKeys } from '@/types';
import useDebouncedCallback from '@/utilities/customHooks/useDebouncedCallback';
import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { ApplicationState } from '../../../../store';
import AlertDialog from '../../../pages/modals/AlertDialog';
import { ApplyToolbar } from '../../ApplyToolbar';
import { SortDirection } from '../types';
import { getColumnsForActiveTab } from './grid-columns';
import { RowEventsHandler } from './grid-columns/formatters';
import { useRows } from './hooks/useRows';
import { TableRow } from './types';
import { useSorting } from './hooks/useSorting';
import { useTagDeletion } from './hooks/useTagDeletion';
import { useColumnWidth } from './hooks/useColumnWidth';

const LEFT_BAR_WIDTH = 200;

export const ApplyDataGrid = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const gridHeaderRef = useRef(null);

  const activeTab = useSelector(({ offersDashboard }: ApplicationState) => offersDashboard.currentTab);

  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const userRole = useSelector(getRole);

  const updateScrollRowIndex = useCallback((idx: number) => dispatch(setScrollRowIndex(idx)), [dispatch]);

  const handleSort = useSorting();

  const { isTagDeleteConfirmationModal, setTagDeleteConfirmationModal, tagDeleteModalData, onTagDelete } =
    useTagDeletion();

  const rows = useRows(onTagDelete);

  const width = LEFT_BAR_WIDTH;

  const columns = useMemo(
    () => getColumnsForActiveTab({ activeTab, userRole }, width),
    [activeTab, userRole], // TODO: reinstate selected rows.
  );

  const gridColumnsWidth = useColumnWidth(columns, gridRef);

  const handleScroll = useDebouncedCallback(e => {
    const rowIdx = Math.round(e.target.lastChild.children[0].offsetTop - 190);
    if (rowIdx) updateScrollRowIndex(rowIdx);
  }, 100);

  const rowKeyGetter = useCallback((row: TableRow) => `${row.offerId}${row.idx}`, []);

  const sortCol = params.get(QueryKeys.SORT_COL) ?? '';
  const sortDirection = params.get(QueryKeys.SORT_DIR) as SortDirection;

  return (
    <>
      <AlertDialog
        isOpen={isTagDeleteConfirmationModal}
        modalTitle="Remove tag"
        descriptionText={`Are you sure you want to remove this tag?`}
        confirmationButtonText={`Remove`}
        id={'remove-tag'}
        onClickSuccess={() => tagDeleteModalData.clickAction()}
        onClose={() => setTagDeleteConfirmationModal(false)}
      />

      <ApplyToolbar />

      <GridContainer ref={gridRef} onScroll={handleScroll}>
        <GridContent
          className="grid-Container"
          role="grid"
          aria-multiselectable="true"
          aria-colcount={columns.length}
          aria-rowcount={rows.length}
        >
          <GridHeader $gridColumnsWidth={gridColumnsWidth} role="row" aria-rowindex={1} ref={gridHeaderRef}>
            {columns.map(column => {
              if (column.headerRenderer) {
                return (
                  <HeaderItem
                    className="grid-HeaderCell"
                    role="columnheader"
                    key={column.key}
                    $isSorted={sortCol === column.key && sortDirection !== 'NONE'}
                  >
                    <column.headerRenderer
                      column={column}
                      sortColumn={sortCol}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      allRowsSelected={false} // TODO: reinstate selected rows.
                      onAllRowsSelectionChange={_checked => {
                        // TODO
                      }}
                    />
                  </HeaderItem>
                );
              }
              return <HeaderItem key={column.key}>{column.name}</HeaderItem>;
            })}
          </GridHeader>
          {rows.map((row, index) => {
            const isRowSelected = false; // TODO
            return (
              <RowEventsHandler
                index={index}
                rowProps={row}
                key={rowKeyGetter(row)}
                gridColumnsWidth={gridColumnsWidth}
                isRowSelected={isRowSelected}
              >
                {columns.map((column, colIndex) => (
                  <RowItem
                    className="grid-Cell"
                    role={colIndex === 1 ? 'rowheader' : 'gridcell'}
                    aria-colindex={colIndex + 1}
                    key={rowKeyGetter(row) + column.key}
                    $isAnySelected={false} // TODO
                    $isSorted={sortCol === column.key && sortDirection !== 'NONE'}
                  >
                    {column.formatter && (
                      <>
                        <column.formatter
                          column={column}
                          rowIdx={Number(column.key)}
                          row={row}
                          isCellSelected={false}
                          isRowSelected={isRowSelected}
                          onRowSelectionChange={_checked => {
                            // TODO
                          }}
                        />
                      </>
                    )}
                  </RowItem>
                ))}
              </RowEventsHandler>
            );
          })}
        </GridContent>
      </GridContainer>
    </>
  );
};

const GridContainer = styled.div`
  height: calc(100vh - 112px);
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  font-style: normal;
`;

const GridContent = styled.div`
  contain: strict;
  display: table;
  min-width: 100%;
`;

const GridHeader = styled.div<{
  $gridColumnsWidth: string;
}>`
  color: var(--ds-colors-muted);
  border-bottom-width: 1px;
  background-color: var(--ds-colors-chakra-body-bg);
  contain: size layout style paint;
  display: grid;
  position: -webkit-sticky;
  position: sticky;
  grid-template-rows: 32px;
  z-index: 4;
  top: 0;
  height: 32px;
  grid-template-columns: ${({ $gridColumnsWidth }) => $gridColumnsWidth};
  align-items: center;
`;

const HeaderItem = styled.div<{
  $isSorted?: boolean;
}>`
  line-height: var(--ds-lineHeights-8);
  letter-spacing: var(--ds-letterSpacings-tight);
  padding: 0 8px;
  ${({ $isSorted }) => ($isSorted ? 'background-color: var(--ds-colors-utility-50);' : '')};

  &:first-child {
    background-color: inherit;
    position: sticky;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 0;
  }
  &:nth-child(2) {
    background-color: inherit;
    position: sticky;
    left: 32px;
    padding-left: 0;
    border-right-width: 1px;
  }
`;

const RowItem = styled.div<{
  $isAnySelected: boolean;
  $isSorted?: boolean;
}>`
  height: 100%;
  overflow: hidden;
  padding: 16px 8px;
  font-size: 14px;
  line-height: 16px;
  color: var(--ds-colors-chakra-body-text);
  ${({ $isSorted }) => ($isSorted ? 'background-color: var(--ds-colors-utility-50);' : null)};

  &:first-child {
    z-index: 1;
    padding: 0;
    background-color: inherit;
    position: sticky;
    left: 0;
  }

  &:nth-child(2) {
    z-index: 1;
    background-color: inherit;
    position: sticky;
    left: 32px;
    padding: 0 12px 0 0;
    border-right-width: 1px;
    ${({ $isSorted }) => ($isSorted ? 'background-color: inherit;' : null)};
  }
`;
